import { type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/provider";
import { useMerchantStore } from "../../store/merchant";
import { UserMenu } from "./user-menu";

type AppBarProps =
  | {
      variant: "dashboard";
      /** Right-aligned pills (Mode/Terminal status, etc.). Optional. */
      pills?: ReactNode;
    }
  | {
      variant: "subpage";
      /** Path to navigate to when the back link is clicked. */
      backTo: string;
      /** Label shown next to the back arrow. */
      backLabel: string;
    };

/**
 * Top navigation bar shared by all pages.
 *
 * - `dashboard` variant: brand logo (links to /dashboard), optional pills slot,
 *   and the user-menu avatar on the right. Used by DashboardPage.
 * - `subpage` variant: a "← <label>" back link in place of the logo, and the
 *   avatar on the right. Used by ProfilePage and any future sub-pages.
 *
 * The user-menu avatar (and its sign-out wiring) is identical across both
 * variants, so a user can always sign out from any page.
 */
export function AppBar(props: AppBarProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const clearMerchant = useMerchantStore((s) => s.clearMerchant);

  async function handleSignOut() {
    await signOut();
    clearMerchant();
    navigate("/login");
  }

  return (
    <div className="topbar">
      {props.variant === "dashboard" ? (
        <Link className="topbar-logo" to="/dashboard">
          <img
            src="/assets/handycashandcards-header.png"
            alt="Handy Cash & Cards"
            className="topbar-logo-img"
          />
        </Link>
      ) : (
        <Link className="topbar-back" to={props.backTo}>
          <span className="topbar-back-arrow" aria-hidden="true">
            &larr;
          </span>
          <span>{props.backLabel}</span>
        </Link>
      )}
      <div className="topbar-right">
        {props.variant === "dashboard" && props.pills && (
          <div className="topbar-pills">{props.pills}</div>
        )}
        {user && (
          <UserMenu
            givenName={user.givenName}
            familyName={user.familyName}
            email={user.email}
            onSignOut={handleSignOut}
          />
        )}
      </div>
    </div>
  );
}
